// import { toast } from '@/hooks/use-toast'
import * as Sentry from '@sentry/react'
import axios, { type AxiosError } from 'axios'
import axiosRetry from 'axios-retry'
import { enqueueSnackbar } from 'notistack'
import { humanizeStatusMessage } from '@/constants/statuses'

const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 15000,
  timeoutErrorMessage: 'Request timed out',
  adapter: 'fetch'
})

http.interceptors.request.use(async req => {
  const token = await window.Clerk.session?.getToken()
  if (!token) {
    window.Clerk.session?.remove()
    return Promise.reject(new Error('No authentication token available'))
  }

  req.headers.Authorization = `Bearer ${token}`

  return req
})

/**
 * Whether the interceptor below toasts this error. Cancellations, timeouts/aborts and status-less
 * network failures are rejected silently, so callers that must not fail quietly check this first.
 */
export const isErrorToastedByHttpClient = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return false
  if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return false
  if (error.code && ['ETIMEDOUT', 'ECONNABORTED'].includes(error.code)) return false
  return Boolean(error.response?.status) && Boolean(error.config?.url)
}

const willBeRetried = (error: AxiosError): boolean => {
  const state = error.config?.['axios-retry']
  if (!state) return false
  const { retryCount = 0, retries = 0, retryCondition } = state
  return retryCount < retries && retryCondition?.(error) === true
}

http.interceptors.response.use(
  res => res.data,
  error => {
    const url: string = error.config?.url
    const status = error.response?.status
    const isFEError = status >= 400 && status < 500

    // CANCELLED REQUEST / TIMEOUT-ABORT / NO STATUS OR URL NETWORK ISSUE
    if (!isErrorToastedByHttpClient(error)) return Promise.reject(error)
    if (willBeRetried(error)) return Promise.reject(error)

    const rawErrorMSG =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      error.response?.data?.error ||
      (typeof error.response?.data === 'string' ? error.response.data : null) ||
      `Server Unreachable ${status}`

    const errorMSG =
      typeof rawErrorMSG === 'string' ? humanizeStatusMessage(rawErrorMSG) : JSON.stringify(rawErrorMSG, null, 2)

    enqueueSnackbar(errorMSG, { variant: 'error' })

    try {
      const body = error.config?.data

      const isJsonHeaders = error.config?.headers?.['Content-Type'] === 'application/json'
      const parcedBody = body && isJsonHeaders ? JSON.parse(body) : body

      const hashedUrl = url
        ?.split('/')
        .map(e => (e.length === 30 ? ':hash' : e))
        .join('/')
      error.name = 'API Error'
      error.message = `URL:${hashedUrl} ${error.message}`

      const name = `API Error : ${error.config?.method.toUpperCase()} ${hashedUrl} ${status}`
      Sentry.captureMessage(isFEError ? name : error, {
        level: isFEError ? 'debug' : 'error',
        contexts: {
          http_error: {
            hasJWT: !!error.config?.headers?.Authorization,
            request: {
              headers: error.config?.headers,
              body: parcedBody
            },
            response: {
              headers: error.response?.headers,
              body: error.response?.data,
              status: error.response?.status
            },
            retryCount: error.config?.['axios-retry']?.retryCount,
            isOnline: window.navigator.onLine,
            code: error.code,
            ALL_DATA: error
          }
        }
      })
    } catch (e) {
      console.error('Error in HTTP response error try method', e)
      Sentry.captureException(e)
      Sentry.captureMessage(error.name, isFEError ? 'debug' : 'error')
    }

    return Promise.reject(error)
  }
)

// SHOULD RETRY AFTER INTERCEPTOR
axiosRetry(http, {
  shouldResetTimeout: true,
  retryCondition: error => {
    // Never retry cancelled requests: React Query aborts superseded queries on
    // purpose (e.g. dashboard search keystrokes) and nothing consumes the response.
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return false
    const status = error.response?.status
    return !status || status >= 500
  },
  onRetry: async (_, error) => {
    console.warn('Retry HTTP API', error.config?.url)
    // await displayConnectionStatusToast()
  }
})

export default http
